import React, { ForwardRefExoticComponent, SVGProps } from 'react'

import { Bullet } from '@factorialco/factorial-one-react/icons/app'
import { FormsElementTypeEnum } from 'generated/resources/datatable.graphql'

import ChoiceElementEditor from 'modules/forms/components/editor/ChoiceElementEditor/ChoiceElementEditor'

import { OptionElementQuestion } from '../../components/QuestionComponent/OptionElementQuestion'
import { Element } from '../Element'
import { Answer, elementParam } from '../types'

/**
 * SingleChoiceElement class for elements with a single selectable option.
 * Extends the base Element class.
 */

export class SingleChoiceElement extends Element {
  /**
   * Get the type of the element
   */
  getType(): FormsElementTypeEnum {
    return 'select'
  }

  /**
   * Get the icon for the element
   */
  getIcon(): ForwardRefExoticComponent<SVGProps<SVGSVGElement>> {
    return Bullet
  }

  /**
   * Validate the element's configuration
   */
  validate(): boolean {
    // Basic validation: label should not be empty
    if (!this.label.trim()) {
      return false
    }

    // There should be at least two options
    const options = this.options
    if (options.length < 2) {
      return false
    }

    // Each option should have a non-empty label
    for (const option of options) {
      if (!option.label.trim()) {
        return false
      }
    }

    return true
  }

  /**
   * Get a React component for editing this element
   *
   * @param _onChange - Callback function to handle changes to the element (unused)
   * @param form - Form data relevant for the update of the elements
   * @returns A React component for editing this element
   */
  getEditorComponent(onChange: (element: Element) => void): React.ReactNode {
    return <ChoiceElementEditor element={this} onChange={onChange} />
  }

  getPreviewComponent(): React.ReactNode {
    return <OptionElementQuestion element={this} isSingleChoice />
  }

  getQuestionComponent(onChange: (answer: Answer) => void): React.ReactNode {
    return <OptionElementQuestion element={this} isSingleChoice onChange={onChange} />
  }

  /**
   * Get whether to display options in a random order
   */
  get randomizeOptions(): boolean {
    return this.getParam<boolean>('randomizeOptions') ?? false
  }

  /**
   * Set whether to display options in a random order
   */
  set randomizeOptions(value: boolean) {
    this.setParam('randomizeOptions', value)
  }

  configureParams(): elementParam[] {
    return [Element.booleanParam('randomizeOptions', 'Randomize')]
  }
}
